<template>
	<div
		class="action-block-container"
		:class="{
			'is-being-converted-to-subflow': isBeingConvertedToSubflow,
			'width-100': !isLoopStart,
			'is-tested-successfully': !isTestedSuccessfully,
		}"
	>
		<v-card
			class="action-block pa-0"
			:class="{
				disabled: disabled,
				'choose-node': isChosen,
				'menu-open': menuOpen,
				'is-edit-name': editNameMode,
				'test-skipped': isTestedSkipped,
				'test-error': isTestedError,
				'is-loop-start': isLoopStart,
				'is-moving': isMoving,
				'dashed-border': isBeingConvertedToSubflow,
				'is-unreachable': isUnreachable,
			}"
			@mouseover="setCurrentHoveredNode(blockData.id)"
			@mouseleave="!menuOpen && (isHovered = false)"
		>
			<div :class="['block-status-mark', { 'display-none': hideStatusMark }]">
				<component v-bind:is="statusMarkComponent"></component>
			</div>

			<FlowTreeBlockNoService style="min-height: 46px; line-height: 46px !important" v-if="!blockData.service" />
			<div class="flex align-center" :class="{ 'flex-centerize': isLoopStart }" v-else>
				<WidgetTypeIcon
					v-if="!isLoopStart"
					class="action-block-icon"
					:serviceSlug="appIcon"
					:miniAppType="blockData?.params_map?.mini_app_type"
				/>
				<div class="action-title-container min-width-0">
					<FlowTreeLoop v-if="isLoopStart" :start-of-loop="isLoopStart" />
					<v-text-field
						v-else-if="editNameMode"
						v-model="blockName"
						ref="editName"
						class="app-text-field edit_name_tf srch ellipsis mt-n1 pl-2 user-select-none"
						hide-details
						single-line
						dense
						@focus="focusBlockName"
						@blur="saveName"
						@keyup.enter="saveName"
						@keyup.esc="cancelName"
					/>
					<!-- PERFORMANCE_OPTIMIZATION - no need of an expensive 'v-text-field' over a simple div. (in Vue 3 add v-memo=[blockName])  -->
					<div v-else class="text-align-left width-100 px-2 text-overflow-ellipsis">{{ blockName }}</div>
				</div>
				<TreeBlockMenu
					v-if="!disabled && !isSelectingWorkflowRoute && isHovered"
					v-on="$listeners"
					class="tree-block-menu-container"
					:blockData="blockData"
					:isFirstBlock="isFirstBlock"
					:isContextualBlock="isContextualBlock"
					:actionData="actionData"
					@editName="editName"
					@skipNode="(d) => $emit(d)"
					v-model="menuOpen"
				/>
			</div>
			<div v-if="isShowInterfacePreview">
				<!-- PERFORMANCE OPTIMIZATION - check for updates only on nodes that being edited -->
				<WidgetPreview
					v-once
					v-if="widgetLoaded"
					:key="blockData.params_map.widget_id"
					:widgetId="blockData.params_map.widget_id"
					:isEndRun="isEndRunWidget"
				/>
			</div>
		</v-card>

		<SubflowSelectionIcon class="subflow-select" :blockData="blockData" />
	</div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';

import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

import BlockPropsComputedsMixin from './BlockPropsComputedsMixin';
import FlowTreeBlockNoService from './FlowTreeBlockNoService.vue';
import FlowTreeLoop from './FlowTreeLoop.vue';
import SubflowSelectionIcon from './helpers/SubflowSelectionIcon';
import TreeBlockMenu from './helpers/TreeBlockMenu';
import WidgetPreview from './helpers/WidgetPreview';
import WidgetTypeIcon from '@/components/common/icons/WidgetTypeIcon';
import { updateNode } from '@/store/modules/nodes/actions';
import { isInterfacePreviewEnabled } from '@/store/modules/selectedWorkflow/getters';
import { removeTempBlock } from '@/store/modules/treeBlocks/actions';
import { isMoveNodeActive, nodeIdToMove } from '@/store/modules/treeBlocks/getters';
import { getWidget } from '@/store/modules/widgets/getters';
import { setLastNodeOfRouteSelection } from '@/store/modules/workflowGraphs/actions';
import {
	isSelectingWorkflowRoute,
	isRouteSelected,
	rootNodeOfRouteSelection,
	lastNodeOfRouteSelection,
	isInSelectedWorkflowRoute,
	isValidRouteEnd,
} from '@/store/modules/workflowGraphs/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import AppActionService from '@/utils/AppActionService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

export default {
	name: 'FlowTreeActionBlock',
	mixins: [BlockPropsComputedsMixin],
	components: {
		FlowTreeLoop,
		FlowTreeBlockNoService,
		WidgetTypeIcon,
		WidgetPreview,
		TreeBlockMenu,
		SubflowSelectionIcon,
	},
	props: {
		blockData: { type: Object },
		isChosen: { type: Boolean },
		isFirstBlock: { type: Boolean },
		disabled: { type: Boolean },
		isContextualBlock: { type: Boolean },
		hideStatus: { type: Boolean, default: false },
		isUnreachable: { type: Boolean, default: false },
		isFinalEndRunBlock: { type: Boolean, default: false },
	},
	data() {
		return {
			editNameMode: false,
			localBlockName: null,
			menuOpen: false,
			isHovered: false,
		};
	},
	computed: {
		...mapGetters([isInterfacePreviewEnabled.fullPath]),
		...mapGettersShorthand({
			isMoveNodeActive,
			nodeIdToMove,
			isSelectingWorkflowRoute,
			isRouteSelected,
			rootNodeOfRouteSelection,
			lastNodeOfRouteSelection,
			isInSelectedWorkflowRoute,
			isValidRouteEnd,
			getWidget,
		}),
		widgetLoaded() {
			return !!this.getWidget(this.blockData.params_map.widget_id);
		},
		statusMarkComponent() {
			return WorkflowNodeService.getTestIcon(this.blockData, this.isChosen);
		},
		blockName: {
			get() {
				if (this.appData && ((!this.editNameMode && !this.blockData.node_title) || (this.blockData.node_title === '' && !this.blockMenu))) {
					if (isEmpty(this.actionData)) {
						return this.appData.service_name;
					}
					return this.actionName;
				} else {
					if (this.editNameMode) {
						return this.localBlockName;
					}
					return this.blockData.node_title;
				}
			},
			set(newValue) {
				this.localBlockName = newValue;
			},
		},
		actionName() {
			return this.actionData?.title;
		},
		isLoopStart() {
			return WorkflowNodeService.isLoopStart(this.blockData, this.actionData);
		},
		appVersion() {
			return this.blockData.service_version;
		},
		appIcon() {
			return (
				AppActionService.getActionIcon({
					action: this.actionData,
					app: this.appData,
					originalApp: this.originalAppData || this.appData,
				}) || ''
			);
		},
		isTestedSuccessfully() {
			return WorkflowNodeService.isTestedSuccessfully(this.blockData);
		},
		isTestedError() {
			return WorkflowNodeService.isTestedError(this.blockData);
		},
		isTestedSkipped() {
			return WorkflowNodeService.isTestedSkipped(this.blockData);
		},
		isWidget() {
			return this.appData?.service_slug === ServiceSlugs.Widgets;
		},
		isEndRunWidget() {
			return AppActionService.isActionOfEndRunWidget(this.actionData);
		},
		isShowInterfacePreview() {
			return this.blockData && (this.isWidget || this.isEndRunWidget) && this[isInterfacePreviewEnabled.fullPath];
		},
		isMoving() {
			return this.isMoveNodeActive && this.nodeIdToMove === this.blockData.id;
		},
		isBeingConvertedToSubflow() {
			const nodeId = String(this.blockData.id);
			if (this.isSelectingWorkflowRoute && nodeId) {
				return this.isInSelectedWorkflowRoute(nodeId) || this.isFirstInSelectedRoute;
			}
			return false;
		},
		isFirstInSelectedRoute() {
			return !!this.rootNodeOfRouteSelection && this.rootNodeOfRouteSelection === this.blockData.id;
		},
		isWebhook() {
			return WorkflowNodeService.isWebhook(this.blockData);
		},
		isLoopEnd() {
			return WorkflowNodeService.isLoopEnd(this.blockData, this.actionData);
		},
		hideStatusMark() {
			if (this.hideStatus) {
				return true;
			}
			if (this.isConsolidateDataNode) {
				// special handling for branches output step, hide statusMark if no mappings are set
				return this.blockData.branch_variables.every((branchVariable) => branchVariable.mappings.length === 0);
			}
			if (this.isLoopEnd) {
				return true;
			}
			if (this.isWebhook) {
				return true;
			}
			return false;
		},
	},
	methods: {
		...mapActionsShorthand({ removeTempBlock, setLastNodeOfRouteSelection, updateNode }),
		saveName() {
			if (this.editNameMode) {
				this.updateNode({ node: { id: this.blockData.id, node_title: this.localBlockName }, partial: true });
				setTimeout(() => this.exitEditNameMode(), 300);
			}
		},
		exitEditNameMode() {
			this.$nextTick(() => {
				this.editNameMode = false;
				this.localBlockName = null;
			});
		},
		cancelName() {
			this.exitEditNameMode();
		},
		focusBlockName() {
			this.$refs.editName.$refs.input.select();
		},
		editName() {
			if (!this.editNameMode) {
				this.localBlockName = this.blockName;
				this.editNameMode = true;
				this.menuOpen = false;
				this.$nextTick(() => {
					this.focusBlockName();
					// this.$refs.editName.focus();
				});
			}
		},
		setCurrentHoveredNode(nodeId) {
			this.isHovered = true;
			if (this.isSelectingWorkflowRoute && !this.isRouteSelected && this.isValidRouteEnd(nodeId)) {
				this.setLastNodeOfRouteSelection(nodeId);
			}
		},
	},
	watch: {
		menuOpen: {
			handler(isOpen) {
				if (isOpen) {
					this.removeTempBlock();
				}
			},
		},
	},
};
</script>

<style lang="scss" scoped>
@import './styles/choose-node-bg.scss';

$radius: 11px;

.action-block-container {
	position: relative;

	&.is-being-converted-to-subflow {
		.action-block {
			background: #f2f4fd !important;
		}
		&.is-tested-successfully {
			.action-block {
				background: #fff8f2 !important;
			}
			.dashed-border::before {
				background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='11' ry='11' stroke='hsl(30, 92%, 59%)' stroke-width='4' stroke-dasharray='10%2c 14' stroke-dashoffset='49' stroke-linecap='square'/%3e%3c/svg%3e") !important;
			}
		}
	}
}
.action-block {
	width: 100%;
	height: auto;
	border-radius: $radius !important;
	box-shadow: 0 0 0 rgba(0, 0, 0, 0) !important;
	border: 1px solid var(--button-border);
	.tree-block-menu-container {
		visibility: 0;
		display: none;
	}
	&:hover,
	&.choose-node,
	&.menu-open {
		transition: all 0.2s;
		.tree-block-menu-container {
			visibility: 1;
			display: block;
		}
	}
	&.disabled {
		pointer-events: none;
	}
	&:not(.disabled) {
		&.choose-node,
		&.menu-open {
			@extend .choose-node-bg;
			position: relative;
		}
	}
	&:hover {
		cursor: pointer !important;
		&:not(.choose-node, .menu-open) {
			background-color: #f9fbfc;

			&:not(.test-skipped, .test-error) {
				border: 1px solid #d6ddebeb;
			}
		}

		&:not(.is-edit-name) {
			::v-deep(.v-input__slot),
			::v-deep(.v-input__slot) input {
				cursor: pointer !important;
			}
		}
	}
	&.test-skipped {
		border-color: #f79836;
	}
	&.test-error {
		border-color: #e94e4e;
	}
	&.test-skipped,
	&.test-error {
		&.choose-node {
			box-shadow: 0 3px 6px rgba(66, 66, 66, 0.3) !important;
		}
	}
	&.is-loop-start {
		display: flex;
		width: 64px !important;
		height: 64px;
		padding: 0 !important;
		transform: rotate(45deg);
	}
}

.action-block-icon {
	height: var(--flow-tree-block-height);
	min-width: 45px;
	width: 45px;
	border-right: 1px solid var(--button-border);
}
.subflow-select {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	left: -30px;
}
.is-moving {
	border: 1px dashed #35c5aa;
}

.dashed-border::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
	width: 100%;
	border-radius: $radius;
	// https://kovart.github.io/dashed-border-generator/
	background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='11' ry='11' stroke='hsl(240, 70%, 63%)' stroke-width='4' stroke-dasharray='10%2c 14' stroke-dashoffset='49' stroke-linecap='square'/%3e%3c/svg%3e");
	z-index: $z-index-1;
}

.is-unreachable {
	opacity: 0.5;

	&:hover {
		cursor: not-allowed !important;
	}
}

.block-status-mark {
	position: absolute;
	z-index: $z-index-2;
	transform: translate(-25%, -30%);

	svg ::v-deep circle {
		fill: #ffffff;
	}
}

.is-loop-start .block-status-mark {
	transform: rotate(-45deg);
	top: auto;
	bottom: -6px;
	right: -4px;
	left: auto;
}
</style>
